import PropTypes from 'prop-types';
import { Bar, Line } from 'react-chartjs-2';
import { CHART_COLORS } from '@/consts';
import { numberFormat } from '@/utils/unitUtils';

function TableView({ headers, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            {headers.map((h, i) => (
              <th
                key={i}
                className={`py-2 px-3 font-medium text-slate-600 ${i === 0 ? 'text-left' : 'text-right'}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b border-slate-100 last:border-0">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`py-2 px-3 text-slate-700 ${ci === 0 ? 'text-left font-medium' : 'text-right tabular-nums'}`}
                >
                  {typeof cell === 'number' ? numberFormat(cell) : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

TableView.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.array).isRequired,
};

function ChartView({ chartType, labels, datasets, xLabel, yLabel }) {
  const chartData = {
    labels,
    datasets: datasets.map((ds, i) => ({
      label: ds.label,
      data: ds.data,
      backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
      borderColor: CHART_COLORS[i % CHART_COLORS.length],
      ...(chartType === 'line' ? { fill: false, tension: 0.1, pointBackgroundColor: CHART_COLORS[i % CHART_COLORS.length] } : {}),
    })),
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: datasets.length > 1,
        position: 'bottom',
      },
    },
    scales: {
      x: {
        title: { display: !!xLabel, text: xLabel ?? '' },
      },
      y: {
        beginAtZero: true,
        title: { display: !!yLabel, text: yLabel ?? '' },
        ticks: { callback: value => numberFormat(value) },
      },
    },
  };

  const Chart = chartType === 'line' ? Line : Bar;
  return <Chart data={chartData} options={options} />;
}

ChartView.propTypes = {
  chartType: PropTypes.string.isRequired,
  labels: PropTypes.array.isRequired,
  datasets: PropTypes.array.isRequired,
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
};

function CustomContentCard({ data }) {
  if (!data || data.error) {
    return (
      <div className="p-4 text-sm text-red-500">
        {data?.error ?? 'No data available.'}
      </div>
    );
  }

  const { title, content_type, data: contentData } = data;

  return (
    <div className="p-5 space-y-4">
      {title && (
        <div>
          <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-0.5">
            {content_type === 'chart' ? 'Chart' : 'Table'}
          </div>
          <div className="text-base font-semibold text-slate-800">{title}</div>
        </div>
      )}

      {content_type === 'table' && contentData?.headers && contentData?.rows && (
        <TableView headers={contentData.headers} rows={contentData.rows} />
      )}

      {content_type === 'chart' && contentData?.labels && contentData?.datasets && (
        <ChartView
          chartType={contentData.chart_type ?? 'bar'}
          labels={contentData.labels}
          datasets={contentData.datasets}
          xLabel={contentData.x_label}
          yLabel={contentData.y_label}
        />
      )}
    </div>
  );
}

CustomContentCard.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    content_type: PropTypes.string,
    data: PropTypes.object,
    error: PropTypes.string,
  }),
};

export default CustomContentCard;
